import React from 'react';
import PropTypes from 'prop-types';
import { Table } from 'antd';

import { capitalize, isEmpty, map, toLower } from '../../../../util/lodashlike';
import {
  MultiHeader,
  SingleHeader,
  SingleDataColumn,
  MultiDataColumn
} from '../Components';
import { ResultTableWrapper } from './styled';
import { normalizeAlertScore } from '../../../../util/normalization';

import { convertISOToDateFormat } from '../../../../util/timezone';
import { TextComponent } from '../../components/TableComponents';
import {
  TM_FRAUD_PREVENTION_SOURCE_MAPPING,
  TM_FRAUD_SOURCE_IDS
} from '../../../../util/GlobalConstant';

const ResultTable = ({
  isLoading,
  results,
  paginationProps,
  onPaginationChange,
  totalResults,
  onAlertIdClick,
  hasAccessToAlertDetails,
  alertLevels
}) => {
  const columns = [
    {
      title: (
        <MultiHeader header="Alert ID" subHeaders={['Alert Generation Date']} />
      ),
      width: '15%',
      ellipsis: true,
      render: (_, record) => {
        const {
          alertId = '-',
          alertDate = '-',
          reviewStatus = 'Not Started',
          alertSourceId
        } = record;

        if (hasAccessToAlertDetails) {
          return (
            <MultiDataColumn.Link
              combined
              data={[
                {
                  text: alertId,
                  to: `/app/trm/run-instance/${alertId}`,
                  ...(toLower(reviewStatus) === 'not started'
                    ? {
                        onClick: () => {
                          onAlertIdClick(alertId, alertSourceId);
                        }
                      }
                    : {})
                },
                {
                  text: convertISOToDateFormat(alertDate) || '-'
                }
              ]}
            />
          );
        } else {
          return (
            <MultiDataColumn
              combined
              data={[
                { text: alertId },
                {
                  text: convertISOToDateFormat(alertDate) || '-'
                }
              ]}
            />
          );
        }
      }
    },

    {
      title: <MultiHeader header="Alert Score" />,
      width: '8%',
      render: (_, record) => {
        const {
          alertLevel = '-',
          alertScore = 0,
          alertLevelLabel = '-',
          alertLevelColor
        } = record;
        const value = normalizeAlertScore(Number(alertScore).toFixed(2));
        return (
          <SingleDataColumn.ProgressBar
            value={value}
            level={alertLevelLabel || capitalize(alertLevel)}
            strokeColor={alertLevelColor}
          />
        );
      }
    },
    {
      title: <MultiHeader header="Party ID" subHeaders={['Party Name']} />,
      width: '15%',
      ellipsis: true,
      render: (_, record) => {
        const { primaryPartyKey = '-', primaryPartyName = '-' } = record;
        return (
          /* `combined` on every MULTI-LINE column (QA, FIN-14012 reopened): the
             lines of one cell are read together — which key belongs to which
             person, which id to which run — and the column widths clip all of
             them. Single-value columns (Alert Score, FinCrime Type, Review
             Status) have nothing to pair and keep the per-line default. */
          <MultiDataColumn
            combined
            data={[{ text: primaryPartyKey }, { text: primaryPartyName }]}
          />
        );
      }
    },
    {
      title: <MultiHeader header="Account NO" subHeaders={['Account Type']} />,
      width: '10%',
      ellipsis: true,
      render: (_, record) => {
        const { accountId = '-', accountType = '-' } = record;
        return (
          <MultiDataColumn
            combined
            data={[{ text: accountId }, { text: accountType }]}
          />
        );
      }
    },

    {
      title: (
        <MultiHeader header="Typology Name" subHeaders={['Typology ID']} />
      ),
      width: '10%',
      ellipsis: true,
      render: (_, record) => {
        const { triggeredTypologies = [] } = record;
        const typologyNames = map(triggeredTypologies, 'name').join(', ');
        const typologyIds = map(triggeredTypologies, 'id').join(', ');
        return (
          <MultiDataColumn
            combined
            data={[
              { text: isEmpty(typologyNames) ? '-' : typologyNames },
              { text: isEmpty(typologyIds) ? '-' : typologyIds }
            ]}
          />
        );
      }
    },
    {
      title: (
        <MultiHeader
          header="Run Instance Name"
          subHeaders={['Run Instance ID', 'Run Instance Execution ID']}
        />
      ),
      width: '13%',
      ellipsis: true,
      render: (_, record) => {
        const {
          runInstanceName = '-',
          runInstanceId = '-',
          runInstanceExecutionId = '-'
        } = record;
        return (
          <MultiDataColumn
            combined
            data={[
              { text: runInstanceName },
              { text: runInstanceId },
              { text: runInstanceExecutionId }
            ]}
          />
        );
      }
    },
    {
      title: <SingleHeader header="FinCrime Type" />,
      width: '9%',
      render: (_, record) => {
        const { alertSourceId = '' } = record;
        return (
          <TextComponent
            id={
              alertSourceId === TM_FRAUD_SOURCE_IDS.TM
                ? TM_FRAUD_PREVENTION_SOURCE_MAPPING.TM_AML
                : alertSourceId === TM_FRAUD_SOURCE_IDS.FRAUD_PREVENTION_ID
                ? TM_FRAUD_PREVENTION_SOURCE_MAPPING.FRAUD_PREVENTION
                : TM_FRAUD_PREVENTION_SOURCE_MAPPING.FRAUD_DETECTION
            }
          />
        );
      }
    },
    {
      title: <SingleHeader header="Review Status" />,
      width: '9%',
      render: (_, record) => {
        const { reviewStatus = 'Not Started' } = record;
        return <SingleDataColumn.Status type={reviewStatus} />;
      }
    },
    {
      title: <SingleHeader header="Review Feedback" />,
      width: '10%',
      render: (_, record) => {
        const { alertQuality = '-' } = record;
        return (
          <SingleDataColumn.Feedback
            type={alertQuality}
            alertLevels={alertLevels}
          />
        );
      }
    }
  ];

  return (
    <ResultTableWrapper>
      <Table
        loading={isLoading}
        columns={columns}
        dataSource={results}
        rowKey="alertId"
        pagination={{
          total: totalResults,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: onPaginationChange,
          showTotal: (total) => `${total} Records`,
          ...paginationProps
        }}
      />
    </ResultTableWrapper>
  );
};

ResultTable.propTypes = {
  results: PropTypes.array,
  isLoading: PropTypes.bool,
  totalResults: PropTypes.number,
  paginationProps: PropTypes.object,
  onPaginationChange: PropTypes.func,
  onAlertIdClick: PropTypes.func,
  alertLevels: PropTypes.array
};

export default ResultTable;
